import React, { useEffect, useState, useMemo, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Link, Unlink, Sparkles, Loader2, Copy, RefreshCw, Webhook, Search, ArrowRight, Zap, History } from "lucide-react";

const fmtPct = (v) => `${Math.round((v || 0) * 100)}%`;
const scoreTone = (s) => {
  if (s >= 0.8) return { bg: "bg-emerald-500", text: "text-white", label: "Haute" };
  if (s >= 0.6) return { bg: "bg-[#FFC60A]", text: "text-zinc-900", label: "Moyenne" };
  return { bg: "bg-zinc-200", text: "text-zinc-900", label: "Faible" };
};

export default function FleetMapping() {
  const [trackers, setTrackers] = useState([]);
  const [trips, setTrips] = useState([]);
  const [search, setSearch] = useState("");
  const [tripSearch, setTripSearch] = useState("");
  const [selectedTracker, setSelectedTracker] = useState(null);
  const [loading, setLoading] = useState(false);
  const [autoMapping, setAutoMapping] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [automapResult, setAutomapResult] = useState(null);
  const [automapLog, setAutomapLog] = useState([]);
  const [showLog, setShowLog] = useState(false);
  const [webhook, setWebhook] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: t }, { data: tr }, { data: w }] = await Promise.all([
        api.get("/logibus/navixy/trackers"),
        api.get("/gtfs/trips"),
        api.get("/logibus/navixy/webhook-info"),
      ]);
      setTrackers(t);
      setTrips(tr);
      setWebhook(w);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filteredTrackers = useMemo(() => {
    const q = search.toLowerCase();
    return trackers.filter((t) => !q || (t.label + " " + t.tracker_id).toLowerCase().includes(q));
  }, [trackers, search]);

  const filteredTrips = useMemo(() => {
    const q = tripSearch.toLowerCase();
    return trips.filter((t) => !q || (t.route_short_name + " " + t.route_long_name + " " + t.trip_headsign).toLowerCase().includes(q));
  }, [trips, tripSearch]);

  const assign = async (tripId) => {
    if (!selectedTracker) return;
    try {
      await api.post("/logibus/navixy/mappings", {
        tracker_id: selectedTracker.tracker_id,
        trip_id: tripId,
        label: selectedTracker.label || "",
      });
      toast.success("Mapping enregistré");
      setSelectedTracker(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message); }
  };

  const removeMapping = async (trackerId) => {
    try {
      await api.delete(`/logibus/navixy/mappings/${trackerId}`);
      toast.success("Mapping retiré");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message); }
  };

  const autoSuggest = async (autoApply = false) => {
    setAutoMapping(true);
    try {
      const { data } = await api.post(`/logibus/navixy/mappings/auto-suggest?auto_apply=${autoApply ? "true" : "false"}`);
      const items = Array.isArray(data) ? data : data.suggestions || [];
      setSuggestions(items);
      setAutomapResult(Array.isArray(data) ? null : data);
      if (autoApply) {
        toast.success(`${data.auto_applied || 0} mapping(s) haute confiance appliqué(s) — ${items.length} suggestion(s) au total`);
        load();
      } else {
        toast.success(`${items.length} suggestion(s) calculée(s) (libellé + GPS + horaire)`);
      }
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message); }
    finally { setAutoMapping(false); }
  };

  const applyOne = async (s) => {
    try {
      await api.post("/logibus/navixy/mappings", { tracker_id: s.tracker_id, trip_id: s.trip_id, label: s.tracker_label });
      toast.success(`Lié: ${s.tracker_label || s.tracker_id} → ${s.route_short_name}`);
      setSuggestions((prev) => prev.filter((x) => x.tracker_id !== s.tracker_id));
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message); }
  };

  const applySuggestions = async (onlyHighConf = false) => {
    const target = onlyHighConf ? suggestions.filter((s) => s.high_confidence) : suggestions;
    if (!target.length) {
      toast.info("Aucune suggestion à appliquer");
      return;
    }
    let ok = 0;
    for (const s of target) {
      try {
        await api.post("/logibus/navixy/mappings", { tracker_id: s.tracker_id, trip_id: s.trip_id, label: s.tracker_label });
        ok += 1;
      } catch { /* skip */ }
    }
    toast.success(`${ok}/${target.length} mappings appliqués`);
    setSuggestions([]);
    setAutomapResult(null);
    load();
  };

  const openLog = async () => {
    try {
      const { data } = await api.get("/logibus/navixy/mappings/automap-log?limit=10");
      setAutomapLog(data);
      setShowLog(true);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message); }
  };

  const copyWebhook = () => {
    if (!webhook?.url) return;
    navigator.clipboard?.writeText(webhook.url.startsWith("http") ? webhook.url : window.location.origin + webhook.url);
    toast.success("URL webhook copiée");
  };

  const rotateWebhook = async () => {
    if (!confirm("Régénérer le token webhook ? L'ancien sera invalide.")) return;
    try {
      await api.post("/logibus/navixy/webhook-info/rotate");
      toast.success("Token webhook régénéré");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message); }
  };

  const mappedCount = trackers.filter((t) => t.mapping).length;

  return (
    <div className="p-4 lg:p-6" data-testid="fleet-mapping-page">
      <header className="flex items-end justify-between flex-wrap gap-3 mb-5">
        <div>
          <div className="text-[10px] font-mono tracking-[0.3em] uppercase text-zinc-500">Logitrak ↔ GTFS</div>
          <h1 className="font-display font-black uppercase text-3xl lg:text-4xl tracking-tighter">Mapping flotte</h1>
          <p className="text-sm text-zinc-600 mt-1">
            Associez chaque tracker Logitrak à une tournée GTFS pour activer le calcul automatique des retards.
            <strong className="ml-2">{mappedCount}/{trackers.length}</strong> trackers mappés.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => autoSuggest(false)} disabled={loading || autoMapping} data-testid="auto-suggest-btn"
            className="inline-flex items-center gap-2 border border-zinc-900 px-3 py-2 text-xs font-bold uppercase tracking-widest hover:bg-zinc-900 hover:text-white disabled:opacity-50">
            {autoMapping ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} Auto-map
          </button>
          <button onClick={() => autoSuggest(true)} disabled={loading || autoMapping} data-testid="auto-apply-btn"
            title="Calcule et applique automatiquement les mappings score ≥ 0.8"
            className="inline-flex items-center gap-2 bg-emerald-600 text-white border border-emerald-700 px-3 py-2 text-xs font-bold uppercase tracking-widest hover:bg-emerald-700 disabled:opacity-50">
            <Zap size={14} /> Auto-apply &gt;0.8
          </button>
          <button onClick={openLog} disabled={loading} data-testid="open-log-btn"
            className="inline-flex items-center gap-2 border border-zinc-900 px-3 py-2 text-xs font-bold uppercase tracking-widest hover:bg-zinc-900 hover:text-white">
            <History size={14} /> Journal
          </button>
          <button onClick={load} disabled={loading} data-testid="refresh-btn"
            className="inline-flex items-center gap-2 bg-[#FFC60A] border border-zinc-900 px-3 py-2 text-xs font-bold uppercase tracking-widest hover:bg-[#ffd43a]">
            {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Rafraîchir
          </button>
        </div>
      </header>

      {/* Webhook config */}
      {webhook && (
        <div className="border border-zinc-900 bg-zinc-50 p-3 mb-4 flex items-center gap-3 flex-wrap" data-testid="webhook-info">
          <Webhook size={16} className="text-zinc-600 shrink-0" />
          <div className="flex-1 min-w-[300px]">
            <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-zinc-500">URL webhook Logitrak (push temps réel)</div>
            <code className="text-xs font-mono text-zinc-900 break-all">{webhook.url.startsWith("http") ? webhook.url : (window.location.origin + webhook.url)}</code>
          </div>
          <button onClick={copyWebhook} data-testid="copy-webhook"
            className="inline-flex items-center gap-1 border border-zinc-900 px-2 py-1 text-[10px] font-bold uppercase tracking-widest hover:bg-zinc-900 hover:text-white">
            <Copy size={11} /> Copier
          </button>
          <button onClick={rotateWebhook} data-testid="rotate-webhook"
            className="inline-flex items-center gap-1 border border-zinc-900 px-2 py-1 text-[10px] font-bold uppercase tracking-widest hover:bg-zinc-900 hover:text-white">
            <RefreshCw size={11} /> Régénérer
          </button>
        </div>
      )}

      {suggestions.length > 0 && (
        <div className="border border-zinc-900 bg-[#FFF7D6] mb-4" data-testid="suggestions-banner">
          <div className="p-3 flex items-center justify-between flex-wrap gap-2 border-b border-zinc-900">
            <div className="font-mono text-xs">
              <strong>{suggestions.length}</strong> suggestion(s)
              {automapResult && automapResult.auto_applied > 0 && (
                <span className="ml-2 text-emerald-700">· {automapResult.auto_applied} auto-appliquée(s) (score ≥ {automapResult.high_conf_threshold})</span>
              )}
              <span className="ml-2 text-zinc-600">· {suggestions.filter((s) => s.high_confidence).length} haute confiance</span>
            </div>
            <div className="flex gap-2 flex-wrap">
              <button onClick={() => { setSuggestions([]); setAutomapResult(null); }} data-testid="dismiss-suggestions"
                className="border border-zinc-900 px-3 py-1 text-[10px] font-bold uppercase tracking-widest">Ignorer</button>
              <button onClick={() => applySuggestions(true)} data-testid="apply-high-conf"
                className="bg-emerald-600 text-white px-3 py-1 text-[10px] font-bold uppercase tracking-widest hover:bg-emerald-700">
                Appliquer haute confiance
              </button>
              <button onClick={() => applySuggestions(false)} data-testid="apply-suggestions"
                className="bg-zinc-900 text-[#FFC60A] px-3 py-1 text-[10px] font-bold uppercase tracking-widest">
                Tout appliquer
              </button>
            </div>
          </div>
          <div className="max-h-[40vh] overflow-y-auto divide-y divide-zinc-200">
            {suggestions.map((s) => {
              const tone = scoreTone(s.score);
              return (
                <div key={s.tracker_id} data-testid={`suggestion-${s.tracker_id}`}
                  className="p-3 flex items-center gap-3 flex-wrap hover:bg-white/60">
                  <div className={`${tone.bg} ${tone.text} px-2 py-1 font-mono text-[10px] font-black uppercase tracking-widest border border-zinc-900 shrink-0`}>
                    {fmtPct(s.score)} · {tone.label}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-display font-bold text-sm tracking-tight truncate">
                      {s.tracker_label || s.tracker_id} → <span className="text-zinc-600">{s.route_short_name}</span> {s.trip_headsign}
                    </div>
                    <div className="text-[10px] font-mono text-zinc-600 flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                      <span>libellé {fmtPct(s.label_score)}</span>
                      <span>GPS {fmtPct(s.geo_score)}</span>
                      <span>horaire {fmtPct(s.time_score)}</span>
                      {s.departure_time && <span>· dép. {String(s.departure_time).slice(0, 5)}</span>}
                    </div>
                    {s.reasons?.length > 0 && (
                      <div className="text-[10px] font-mono text-zinc-500 mt-0.5 truncate">{s.reasons.join(" · ")}</div>
                    )}
                  </div>
                  <button onClick={() => applyOne(s)} data-testid={`apply-one-${s.tracker_id}`}
                    className="border border-zinc-900 px-2 py-1 text-[10px] font-bold uppercase tracking-widest hover:bg-zinc-900 hover:text-white shrink-0">
                    Appliquer
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {showLog && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="automap-log-modal"
          onClick={(e) => { if (e.target === e.currentTarget) setShowLog(false); }}>
          <div className="bg-white border border-zinc-900 max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
            <div className="p-3 border-b border-zinc-900 bg-zinc-900 text-white flex items-center justify-between">
              <div className="font-display font-bold text-sm tracking-tight uppercase">Journal Auto-map</div>
              <button onClick={() => setShowLog(false)} data-testid="close-log"
                className="text-[10px] font-mono uppercase tracking-widest hover:text-[#FFC60A]">Fermer ✕</button>
            </div>
            <div className="overflow-y-auto">
              {automapLog.length === 0 && (
                <div className="p-6 text-xs text-zinc-500 text-center">Aucune exécution auto-apply enregistrée.</div>
              )}
              {automapLog.map((l) => (
                <div key={l.id} className="border-b border-zinc-200 p-3 text-xs">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="font-mono text-zinc-900">{new Date(l.at).toLocaleString("fr-FR")}</div>
                    <div className="font-mono text-emerald-700">{l.applied_count}/{l.total_suggestions} appliqués · seuil {l.high_conf_threshold}</div>
                  </div>
                  <div className="text-[10px] font-mono text-zinc-500 mt-1 truncate">par {l.user_email || "—"}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-0 border border-zinc-900 min-h-[60vh]">
        {/* Trackers column */}
        <section className="border-r border-zinc-900" data-testid="trackers-col">
          <div className="p-3 border-b border-zinc-900 bg-zinc-900 text-white flex items-center gap-2">
            <Search size={12} />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Filtrer trackers Logitrak"
              data-testid="tracker-search" className="flex-1 bg-transparent outline-none text-xs font-mono text-white placeholder:text-zinc-500" />
            <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-400">{filteredTrackers.length}</span>
          </div>
          <div className="max-h-[60vh] overflow-y-auto">
            {filteredTrackers.length === 0 && (
              <div className="p-6 text-xs text-zinc-500 text-center">Aucun tracker — Logitrak hash configuré ?</div>
            )}
            {filteredTrackers.map((t) => {
              const active = selectedTracker?.tracker_id === t.tracker_id;
              return (
                <div key={t.tracker_id} data-testid={`tracker-${t.tracker_id}`}
                  className={`border-b border-zinc-200 p-3 ${active ? "bg-[#FFF7D6]" : "hover:bg-zinc-50"}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="font-display font-bold text-sm tracking-tight truncate flex items-center gap-2">
                        {t.label || t.tracker_id}
                        {String(t.tracker_id).startsWith("sim-") ? (
                          <span className="px-1 py-0.5 text-[8px] uppercase tracking-widest bg-orange-100 text-orange-800 border border-orange-300">DÉMO</span>
                        ) : (
                          <span className="px-1 py-0.5 text-[8px] uppercase tracking-widest bg-green-100 text-green-800 border border-green-300">GPS</span>
                        )}
                      </div>
                      <div className="text-[10px] font-mono text-zinc-500">{t.tracker_id} · {t.lat?.toFixed(4)}, {t.lon?.toFixed(4)} · {t.movement_status || "—"}</div>
                      {t.mapping && (
                        <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-zinc-900 bg-[#FFC60A] border border-zinc-900 px-1.5 py-0.5">
                          → {t.mapping.trip_id}
                        </div>
                      )}
                    </div>
                    <div className="flex gap-1">
                      {t.mapping ? (
                        <button onClick={() => removeMapping(t.tracker_id)} data-testid={`unlink-${t.tracker_id}`}
                          title="Retirer le mapping"
                          className="border border-zinc-900 px-2 py-1 text-[10px] font-bold hover:bg-red-500 hover:text-white">
                          <Unlink size={11} />
                        </button>
                      ) : (
                        <button onClick={() => setSelectedTracker(active ? null : t)} data-testid={`pick-${t.tracker_id}`}
                          className={`border border-zinc-900 px-2 py-1 text-[10px] font-bold ${active ? "bg-zinc-900 text-[#FFC60A]" : "bg-white hover:bg-zinc-900 hover:text-white"}`}>
                          {active ? "Sélectionné" : "Lier…"}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Trips column */}
        <section data-testid="trips-col">
          <div className="p-3 border-b border-zinc-900 bg-zinc-900 text-white flex items-center gap-2">
            <Search size={12} />
            <input value={tripSearch} onChange={(e) => setTripSearch(e.target.value)} placeholder="Filtrer tournées GTFS"
              data-testid="trip-search" className="flex-1 bg-transparent outline-none text-xs font-mono text-white placeholder:text-zinc-500" />
            <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-400">{filteredTrips.length}</span>
          </div>
          {!selectedTracker && (
            <div className="p-6 text-xs text-zinc-500 text-center flex flex-col items-center gap-2">
              <ArrowRight size={18} className="text-zinc-300" />
              Sélectionnez un tracker à gauche pour voir les tournées à lier.
            </div>
          )}
          {selectedTracker && (
            <div className="max-h-[60vh] overflow-y-auto" data-testid="trip-pick-list">
              <div className="bg-[#FFF7D6] border-b border-zinc-900 p-3 text-xs">
                Lier <strong>{selectedTracker.label}</strong> à :
              </div>
              {filteredTrips.map((t) => (
                <button key={t.trip_id} onClick={() => assign(t.trip_id)} data-testid={`pick-trip-${t.trip_id}`}
                  className="w-full text-left border-b border-zinc-200 p-3 hover:bg-[#FFC60A]/30 flex items-center gap-2">
                  <div className="w-9 h-9 flex items-center justify-center font-display font-black text-xs border border-zinc-900"
                    style={{ background: `#${t.color || "FFC60A"}`, color: t.color === "0A0A0B" ? "#FFC60A" : "#0A0A0B" }}>
                    {t.route_short_name}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-bold text-sm tracking-tight truncate">{t.route_long_name}</div>
                    <div className="text-[10px] font-mono text-zinc-500 truncate">→ {t.trip_headsign} · {t.departure_time?.slice(0,5)} → {t.arrival_time?.slice(0,5)} · {t.stops_count} arrêts</div>
                  </div>
                  <Link size={14} className="text-zinc-600" />
                </button>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
